import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { useRoutePortalTarget } from './layout/RoutePortalHost';

export type RoddoConfirmTone = 'danger' | 'warning' | 'info' | 'success';

export type RoddoDialogActionTone = 'primary' | 'neutral' | 'danger' | 'warning' | 'info';

export interface RoddoDialogAction {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  tone?: RoddoDialogActionTone;
  disabled?: boolean;
  onClick: () => void | Promise<void>;
}

interface RoddoConfirmDialogProps {
  open: boolean;
  title: string;
  eyebrow?: string;
  description?: string;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: RoddoConfirmTone;
  isBusy?: boolean;
  isDarkMode?: boolean;
  icon?: React.ReactNode;
  showCancel?: boolean;
  closeOnBackdrop?: boolean;
  actions?: RoddoDialogAction[];
  onConfirm?: () => void | Promise<void>;
  onCancel: () => void;
}

const toneStyles = (tone: RoddoConfirmTone, isDark: boolean) => {
  if (tone === 'danger') {
    return {
      icon: '🗑️',
      accent: '#EF6A73',
      soft: isDark ? 'rgba(239,106,115,0.16)' : 'rgba(255,228,230,0.92)',
      text: isDark ? '#ffe4e6' : '#7f1d1d',
      button: '#EF6A73',
      buttonText: '#5f111f',
    };
  }

  if (tone === 'success') {
    return {
      icon: '✓',
      accent: '#66D6A9',
      soft: isDark ? 'rgba(102,214,169,0.14)' : 'rgba(220,252,231,0.96)',
      text: isDark ? '#DDFBEF' : '#14532d',
      button: isDark ? '#66D6A9' : '#2FAE79',
      buttonText: isDark ? '#08261D' : '#ffffff',
    };
  }

  if (tone === 'warning') {
    return {
      icon: '⚠️',
      accent: '#F2B84B',
      soft: isDark ? 'rgba(242,184,75,0.16)' : 'rgba(254,243,199,0.95)',
      text: isDark ? '#fef3c7' : '#78350f',
      button: '#F2B84B',
      buttonText: '#51320a',
    };
  }

  return {
    icon: '✨',
    accent: '#66D9E8',
    soft: isDark ? 'rgba(102,217,232,0.14)' : 'rgba(219,234,254,0.95)',
    text: isDark ? '#DDF7FA' : '#1e3a8a',
    button: isDark ? '#7057E8' : '#2F80ED',
    buttonText: '#102a5f',
  };
};

const RoddoConfirmDialog: React.FC<RoddoConfirmDialogProps> = ({
  open,
  title,
  eyebrow,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  isBusy = false,
  isDarkMode,
  icon,
  showCancel = true,
  closeOnBackdrop = true,
  actions,
  onConfirm,
  onCancel,
}) => {
  const portalTarget = useRoutePortalTarget();
  const isDark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(isDark);
  const toneUi = toneStyles(tone, isDark);
  const hasChoices = Array.isArray(actions) && actions.length > 0;

  const actionStyleFor = (actionTone: RoddoDialogActionTone = 'neutral') => {
    if (actionTone === 'danger') {
      return {
        background: '#EF6A73',
        borderColor: '#EF6A73',
        color: '#5f111f',
      };
    }
    if (actionTone === 'warning') {
      return {
        background: '#F2B84B',
        borderColor: '#F2B84B',
        color: '#51320a',
      };
    }
    if (actionTone === 'info' || actionTone === 'primary') {
      return {
        background: isDark ? '#7057E8' : '#2F80ED',
        borderColor: isDark ? 'rgba(202,194,255,0.42)' : '#66D9E8',
        color: isDark ? '#ffffff' : '#102a5f',
      };
    }
    return {
      background: ui.surfaceBgSoft ?? ui.cardBg,
      borderColor: ui.border,
      color: ui.textPrimary,
    };
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isBusy) onCancel();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isBusy, onCancel]);

  if (!open || !portalTarget) return null;

  return createPortal(
    <div
      className="fixed left-0 right-0 z-[99999] grid min-h-0 place-items-center bg-black/55 p-4 backdrop-blur-sm"
      style={{ top: "calc(var(--roddo-header-h, 96px) + 8px)", bottom: "calc(var(--roddo-nav-h, 84px) + 8px)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="roddo-confirm-title"
      onMouseDown={(event) => {
        if (!closeOnBackdrop || isBusy) return;
        if (event.currentTarget === event.target) onCancel();
      }}
    >
      <div
        className="max-h-full w-full max-w-[380px] overflow-y-auto overscroll-contain rounded-[18px] border p-4 shadow-2xl"
        style={{
          background: ui.cardBgStrong ?? ui.cardBg,
          borderColor: ui.borderStrong ?? ui.border,
          color: ui.textPrimary,
          boxShadow: `${ui.shadowStrong}, inset 0 1px 0 rgba(255,255,255,0.18)`,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px] border text-xl shadow-none"
            style={{ background: toneUi.soft, borderColor: toneUi.accent, color: toneUi.text }}
            aria-hidden="true"
          >
            {icon ?? toneUi.icon}
          </div>
          <div className="min-w-0 flex-1">
            {eyebrow ? (
              <div className="mb-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: toneUi.accent }}>
                {eyebrow}
              </div>
            ) : null}
            <h2 id="roddo-confirm-title" className="text-lg font-black leading-tight">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-sm font-semibold leading-5" style={{ color: ui.textSecondary }}>
                {description}
              </p>
            ) : null}
          </div>
        </div>

        {children ? (
          <div className="mt-4">{children}</div>
        ) : null}

        {hasChoices ? (
          <div className="mt-4 grid gap-2">
            {actions!.map((action, index) => {
              const actionStyle = actionStyleFor(action.tone);
              return (
                <button
                  key={`${action.label}-${index}`}
                  type="button"
                  disabled={isBusy || action.disabled}
                  onClick={() => void action.onClick()}
                  className="flex w-full items-center gap-3 rounded-[12px] border px-4 py-3 text-left transition hover:-translate-y-0.5 active:translate-y-px disabled:opacity-60"
                  style={{
                    ...actionStyle,
                    boxShadow:
                      action.tone === 'primary' || action.tone === 'info'
                        ? '0 8px 18px rgba(112,87,232,0.16), inset 0 1px 0 rgba(255,255,255,0.16)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.10)',
                  }}
                >
                  {action.icon ? (
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[14px] border border-current/10 bg-white/10 text-lg">
                      {action.icon}
                    </span>
                  ) : null}

                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-black">{action.label}</span>
                    {action.description ? (
                      <span className="mt-0.5 block text-[11px] font-semibold leading-4 opacity-70">
                        {action.description}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}

            {showCancel ? (
              <button
                type="button"
                disabled={isBusy}
                onClick={onCancel}
                className="mt-1 rounded-[12px] border px-4 py-3 text-sm font-black transition active:translate-y-px disabled:opacity-60"
                style={{
                  background: ui.surfaceBgSoft ?? ui.cardBg,
                  borderColor: ui.border,
                  color: ui.textPrimary,
                }}
              >
                {cancelLabel}
              </button>
            ) : null}
          </div>
        ) : (
          <div className={`mt-4 grid gap-2 ${showCancel ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {showCancel ? (
              <button
                type="button"
                disabled={isBusy}
                onClick={onCancel}
                className="rounded-[12px] border px-4 py-3 text-sm font-black transition active:translate-y-px disabled:opacity-60"
                style={{
                  background: ui.surfaceBgSoft ?? ui.cardBg,
                  borderColor: ui.border,
                  color: ui.textPrimary,
                }}
              >
                {cancelLabel}
              </button>
            ) : null}
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void onConfirm?.()}
              className="rounded-[12px] border px-4 py-3 text-sm font-black  transition active:translate-y-px disabled:opacity-60"
              style={{
                background: toneUi.button,
                borderColor: toneUi.accent,
                color: toneUi.buttonText,
              }}
            >
              {isBusy ? 'Working…' : confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    portalTarget,
  );
};

export default RoddoConfirmDialog;
